import { baseLocale, getLocale, type Locale } from '../paraglide/runtime';

/**
 * Catalog data text in the reader's language: SKU descriptions, input field
 * descriptions, endpoint action titles, editorial copy, category blurbs, addon
 * labels and the benchmark latency note. None of it is ours to put in
 * `messages/`: most comes from the gateway, and all of it changes with the
 * catalog.
 *
 * Each translated locale has a translation memory, `catalog/{locale}.json`,
 * keyed by the exact English source text. A miss returns the English, so a
 * manifest whose wording changes shows the new English until someone
 * translates it, and no catalog change ever waits on a translation.
 * `scripts/catalog-i18n-worklist.ts` lists every string these pages can show.
 *
 * How the memory reaches a render. Each file is its own chunk, imported only
 * for a translated locale, so an English page never downloads one. Lookup is
 * synchronous; the async part is `loadCatalogText`, awaited once per document
 * before anything renders that reads catalog text:
 *
 * - server: the catalog route loaders (`features/catalog/queries.ts`), which
 *   run before the render that becomes the HTML;
 * - browser, first render: the router's `hydrate` hook (`src/router.tsx`),
 *   which TanStack awaits before hydrating, so hydration sees the same strings
 *   the HTML was rendered with. It loads only when a matched route declares
 *   `staticData.catalogText`, so a page that reads none (the homepage) does not
 *   wait on the download;
 * - client navigation: the same route loaders, which run in the browser before
 *   the page they load renders.
 */

type Memory = Readonly<Record<string, string>>;
type TranslatedLocale = Exclude<Locale, typeof baseLocale>;

const MEMORY: Record<TranslatedLocale, () => Promise<{ default: Memory }>> = {
  'zh-Hans': () => import('./catalog/zh-Hans.json'),
  'pt-BR': () => import('./catalog/pt-BR.json'),
  es: () => import('./catalog/es.json'),
  fr: () => import('./catalog/fr.json'),
  de: () => import('./catalog/de.json'),
  ru: () => import('./catalog/ru.json'),
  it: () => import('./catalog/it.json'),
};

/** Static bundled data, so one copy serves every request a Worker handles. */
const loaded: Partial<Record<TranslatedLocale, Memory>> = {};

/** Loads the current locale's memory; does nothing for English. The module
 *  loader fetches each chunk once, so calling this again is cheap. */
export const loadCatalogText = async (): Promise<void> => {
  const locale = getLocale();
  if (locale === baseLocale) return;
  loaded[locale] ??= (await MEMORY[locale]()).default;
};

/** Loads the memory when a matched route reads catalog text. */
export const loadCatalogTextFor = async (
  matches: readonly { staticData?: { catalogText?: boolean } }[],
): Promise<void> => {
  if (matches.some((match) => match.staticData?.catalogText)) await loadCatalogText();
};

/** One catalog data string in the locale of the render, or the English on a miss. */
export const catalogText = (english: string): string => {
  const locale = getLocale();
  if (locale === baseLocale) return english;
  return loaded[locale]?.[english] ?? english;
};
